import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { buildWebSocketUrl } from '../../utils/websocket';
import { useAuth } from '../../contexts/AuthContext';
import VoteRound from './VoteRound';
import VetoTimeline from './VetoTimeline';
import VetoResults from './VetoResults';
import './VetoFlow.css';

/**
 * Interactive veto flow with WebSocket updates, timer fallback, and voting.
 */
export default function VetoFlow({ matchId, initialState, onComplete, gameMode, weaponMode }) {
  const { t } = useTranslation();
  const { token, getAuthHeaders } = useAuth();
  // Ref så att en reconnect alltid identifierar med senaste token
  // (WS-effekten beror bara på matchId).
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [vetoState, setVetoState] = useState(initialState || null);
  // Lagets val i aktuell omgång, så som servern har dem ({discord_id: [maps] | 'ct'/'t'}).
  // Kommer bara till spelare i det röstande laget (state + teamkanalen).
  const [teamSelections, setTeamSelections] = useState({ round: -1, selections: {} });
  // Mitt senast sparade löpnummer (för att fortsätta efter reload).
  const [mySeq, setMySeq] = useState(null);
  // Senaste state-hämtningen misslyckades. Visas bara som info — flödet
  // fortsätter som förut (WS-reconnect och timer-pollning hämtar om).
  const [stateError, setStateError] = useState(false);
  const [mapStrengths, setMapStrengths] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // Kart-ELO-hints (fas 4C): hämtas en gång; backend svarar enabled=false
  // (→ inga badges) tills MAP_ELO_VETO_HINTS_ENABLED slås på i admin.
  useEffect(() => {
    fetch(`${API_URL}/api/veto/${matchId}/map-strengths`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.enabled) setMapStrengths(data.maps || {});
      })
      .catch(() => {
        // tyst — hinten är icke-kritisk för vetot
      });
  }, [matchId]);

  // Fetch state on mount (reconnect support)
  const fetchState = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/veto/${matchId}/state`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const state = await response.json();
        setVetoState(state);
        setTeamSelections({ round: state.current_round, selections: state.team_selections || {} });
        setMySeq(state.my_seq ?? null);
        setStateError(false);
      } else {
        console.error('Failed to fetch veto state: HTTP %d', response.status);
        setStateError(true);
      }
    } catch (err) {
      console.error('Failed to fetch veto state:', err);
      setStateError(true);
    }
  }, [matchId, getAuthHeaders]);

  useEffect(() => {
    if (!vetoState) fetchState();
  }, [vetoState, fetchState]);

  // Poll for state when timer expires (fallback if WS misses the update)
  useEffect(() => {
    if (!vetoState || vetoState.phase === 'complete') return;
    const currentRound = vetoState.rounds?.[vetoState.current_round];
    if (!currentRound || currentRound.status !== 'active' || !currentRound.timer_start) return;

    // State-anropet stänger en utgången omgång på servern (timer + 1 s), så
    // ett anrop strax efter räcker även om WS-händelsen uteblir.
    const expiry = (currentRound.timer_start + (currentRound.timer_duration || 30) + 2) * 1000;
    const delay = expiry - Date.now();
    if (delay < 0) {
      // Already expired — poll immediately
      fetchState();
      return;
    }
    const timer = setTimeout(() => fetchState(), delay);
    return () => clearTimeout(timer);
  }, [vetoState?.current_round, vetoState?.phase, fetchState]);

  // WebSocket connection
  useEffect(() => {
    if (!matchId) return;
    // Sätts vid avmontering så att close() där inte startar en ny anslutning.
    let stopped = false;

    const connect = () => {
      const wsUrl = buildWebSocketUrl(`/api/veto/ws/${matchId}`);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('Veto WebSocket connected');
        // Identify with our JWT — the server derives discord_id from it
        // and never trusts a client-supplied id.
        if (tokenRef.current) {
          ws.send(JSON.stringify({ type: 'identify', token: tokenRef.current }));
        }
        // Re-fetch state on reconnect to sync any missed updates
        fetchState();
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          handleWsEvent(data);
        } catch (err) {
          console.error('Failed to parse veto WS message:', err);
        }
      };

      // Heartbeat
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      ws.onclose = () => {
        clearInterval(pingInterval);
        if (stopped) return;
        console.log('Veto WebSocket closed, reconnecting...');
        reconnectRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = (err) => {
        console.error('Veto WebSocket error:', err);
        ws.close();
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      stopped = true;
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [matchId]);

  const handleWsEvent = (data) => {
    const { event } = data;

    console.log('[veto-ws] event=%s data=%o', event, data);
    // En händelse från servern betyder att vi är i synk igen.
    setStateError(false);

    if (event === 'veto_started') {
      console.log('[veto-ws] veto_started — setting initial state');
      setVetoState(data.state);
    } else if (event === 'vote_cast') {
      console.log('[veto-ws] vote_cast round=%d team=%s count=%d/%d voted=%o', data.round, data.team, data.vote_count, data.total_players, data.voted_ids);
      // Update vote count — only if the event is for the current round
      setVetoState(prev => {
        if (!prev) return prev;
        if (data.round !== undefined && data.round !== prev.current_round) {
          console.log('[veto-ws] vote_cast ignored: event round=%d != current=%d', data.round, prev.current_round);
          return prev;
        }
        const rounds = [...prev.rounds];
        const current = { ...rounds[prev.current_round] };
        current.votes = {
          _count: data.vote_count,
          _voted_ids: data.voted_ids || current.votes?._voted_ids || [],
          _complete_ids: data.complete_ids || current.votes?._complete_ids || [],
        };
        rounds[prev.current_round] = current;
        return { ...prev, rounds };
      });
    } else if (event === 'round_complete') {
      console.log('[veto-ws] round_complete round=%d → next=%d maps_remaining=%o',
        data.round?.round_num, data.current_round, data.maps_remaining);
      // Ny omgång: lagets val börjar om
      setTeamSelections({ round: -1, selections: {} });
      // Update state with round results
      setVetoState(prev => {
        if (!prev) return prev;
        const rounds = [...prev.rounds];
        // Find the completed round
        const roundIdx = data.round.round_num;
        rounds[roundIdx] = data.round;
        // Apply next round from server (includes timer_start)
        const nextIdx = data.current_round ?? (roundIdx + 1);
        if (data.next_round && nextIdx < rounds.length) {
          rounds[nextIdx] = data.next_round;
          console.log('[veto-ws] next round data: type=%s team=%s status=%s',
            data.next_round.type, data.next_round.team, data.next_round.status);
        } else if (nextIdx < rounds.length) {
          rounds[nextIdx] = { ...rounds[nextIdx], status: 'active' };
          console.log('[veto-ws] activated next round %d (no server data)', nextIdx);
        } else {
          console.log('[veto-ws] no more rounds (nextIdx=%d, total=%d)', nextIdx, rounds.length);
        }
        return {
          ...prev,
          rounds,
          current_round: nextIdx,
          maps_remaining: data.maps_remaining,
          banned_maps: data.banned_maps,
          picked_maps: data.picked_maps,
        };
      });
    } else if (event === 'team_selections') {
      setTeamSelections({ round: data.round, selections: data.selections || {} });
    } else if (event === 'veto_complete') {
      console.log('[veto-ws] veto_complete picked=%o', data.picked_maps);
      setVetoState(prev => ({
        ...prev,
        phase: 'complete',
        maps_remaining: data.maps_remaining,
        picked_maps: data.picked_maps,
        banned_maps: data.banned_maps,
      }));
    }
  };

  if (!vetoState) {
    return (
      <>
        <div className="veto-loading">{t('veto.loading')}</div>
        {stateError && <div className="veto-error">{t('veto.stateFetchFailed')}</div>}
      </>
    );
  }

  const isComplete = vetoState.phase === 'complete' ||
    vetoState.current_round >= vetoState.rounds.length;

  return (
    <div className="veto-flow">
      {/* Inte på resultatsidan: Redis-staten går ut (TTL) efter vetot och en
          sen reconnect ger 404, men resultatet ligger redan lokalt. */}
      {stateError && !isComplete && (
        <div className="veto-error">{t('veto.stateFetchFailed')}</div>
      )}
      <div className="veto-mode-label">
        {vetoState.veto_mode === 'team_vote' ? t('veto.modeTeamVote') : t('veto.modeCaptainFull')}
        {' \u2014 '}
        {vetoState.match_type}
      </div>

      <VetoTimeline
        rounds={vetoState.rounds}
        currentRound={vetoState.current_round}
        vetoState={vetoState}
      />

      {isComplete ? (
        <VetoResults
          vetoState={vetoState}
          onContinue={onComplete}
          gameMode={gameMode}
          weaponMode={weaponMode}
        />
      ) : (
        <VoteRound
          vetoState={vetoState}
          matchId={matchId}
          teamSelections={teamSelections.round === vetoState.current_round ? teamSelections.selections : {}}
          initialSeq={mySeq}
          mapStrengths={mapStrengths}
        />
      )}
    </div>
  );
}
